import { cloneElement, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';

export type TooltipTarget = ReactElement<{ 'aria-label'?: string }>;

interface Props {
  label: string;
  side?: 'top' | 'right' | 'bottom';
  children: TooltipTarget;
}

/** Einfacher Tooltip (Hover/Fokus). Setzt `aria-label` am Kind, wenn es keins hat. */
export function Tooltip({ label, side = 'top', children }: Props) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = (el: Element) => {
    const r = el.getBoundingClientRect();
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (side === 'right') setPos({ x: r.right + 10, y: r.top + r.height / 2 });
      else if (side === 'bottom') setPos({ x: r.left + r.width / 2, y: r.bottom + 8 });
      else setPos({ x: r.left + r.width / 2, y: r.top - 8 });
    }, 250);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setPos(null);
  };
  const style: React.CSSProperties = pos
    ? side === 'right'
      ? { left: pos.x, top: pos.y, transform: 'translateY(-50%)' }
      : side === 'bottom'
        ? { left: pos.x, top: pos.y, transform: 'translateX(-50%)' }
        : { left: pos.x, top: pos.y, transform: 'translate(-50%, -100%)' }
    : {};
  const child = children.props['aria-label'] ? children : cloneElement(children, { 'aria-label': label });
  return (
    <span
      className="tooltip-anchor"
      onMouseEnter={(e) => show(e.currentTarget)}
      onMouseLeave={hide}
      onFocus={(e) => show(e.currentTarget)}
      onBlur={hide}
      onClick={hide}
    >
      {child}
      {pos &&
        createPortal(
          <div className="tooltip" role="tooltip" style={style}>
            {label}
          </div>,
          document.body,
        )}
    </span>
  );
}
